/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';

import { Layout, Button, Table, DatePicker, Modal, Row, Col, Input, Card, Space, Tag, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';

import useAuth from 'hooks/useAuth';
import { userDelete, getTeamUsers } from 'apis/user';

import moment from 'moment';

import './styles.less';

const { RangePicker } = DatePicker;

const Users = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState([]);

  const searchInputEl = useRef(null);
  const [, setUsers] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const [usersFilter, setUsersFilter] = useState([]);
  const [expandedKey, setExpandedKey] = useState([]);
  const onExpand = (_, { userId }) => expandedKey.includes(userId) ? setExpandedKey(prev => prev.filter(item => item !== userId)) : setExpandedKey(prev => [...prev, userId]);

  const [searchText, setSearchText] = useState('');
  const [, setSearchedColumn] = useState('');

  const [fromDate, setFromDate] = useState(moment().subtract(12, 'months'));
  const [toDate, setToDate] = useState(moment());
  const [email, setEmail] = useState("");

  const fetchTeamUsers = async (isLoadMore = false) => {
    try {
      setLoading(true);
      const payload = {
        limit: 200,
      };

      if (email) {
        payload.email = email.trim();
      } else {
        payload.fromDate = fromDate.format("YYYY-MM-DD");
        payload.toDate = toDate.format("YYYY-MM-DD");
      }

      if (isLoadMore && lastVisible) {
        payload.lastVisible = lastVisible;
      }

      const result = await getTeamUsers(payload);

      const newUsers = result?.data?.users || [];
      const nextCursor = result?.data?.lastVisible || null;

      if (isLoadMore) {
        setUsers(prev => [...prev, ...newUsers]);
        setUsersFilter(prev => [...prev, ...newUsers]);
      } else {
        setUsers(newUsers);
        setUsersFilter(newUsers);
      }

      setLastVisible(nextCursor);
      setHasMore(!!nextCursor);
    } catch (e) {
      console.error(e);
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchTeamUsers(false);
    }
  }, [user]);

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
      return (
        <div style={{ padding: 8 }}>
          <Input
            ref={searchInputEl}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button onClick={() => handleReset(clearFilters, confirm)} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) => {
      const [firstValue, ...restValue] = value;

      if (dataIndex === "usernameReferral" && firstValue === "!") {
        return record[dataIndex]
          ? !record[dataIndex].toString().toLowerCase().includes(restValue.join("").toLowerCase())
          : true;
      }

      return record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '';
    },
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInputEl.current.select(), 100);
      }
    },
    render: text => {
      return (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <div>
            <div>
              <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchText]}
                autoEscape
                textToHighlight={text[dataIndex] ? (dataIndex === "source" ? `${text[dataIndex].charAt(0).toUpperCase()}${text[dataIndex].slice(1)}`.toString() : text[dataIndex].toString()) : ''}
              />
            </div>
          </div>
        </div>
      )
    },
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText('');
    confirm();
  };

  function onChange(dates, dateStrings) {
    if (!dates) {
      setFromDate(undefined);
      setToDate(undefined);
    }

    if (dates && dates[0]) {
      setFromDate(dates[0])
    }

    if (dates && dates[1]) {
      setToDate(dates[1])
    }
  }

  const columns = [
    {
      title: 'Country',
      align: 'center',
      width: '5%',
      ...getColumnSearchProps('countryCode'),
    },
    {
      title: 'Created At',
      align: 'center',
      dataIndex: 'createdAt',
      width: '10%',
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      defaultSortOrder: 'descend',
      render: (text, record, index) => {
        return (
          <div>
            <div>{moment(record.createdAt).format('YYYY-MM-DD')}</div>
          </div>
        );
      }
    },
    {
      title: 'Email',
      width: '30%',
      ...getColumnSearchProps('email'),
    },
    {
      title: 'Source',
      width: '8%',
      ...getColumnSearchProps('source'),
    },
    {
      title: 'Role',
      align: 'center',
      render: (record) => {
        if (record?.role === 'free') {
          return (
            <Tag>{record?.role?.toUpperCase()}</Tag>
          );
        } else {
          return (
            <Tag color='green'>{record?.role?.toUpperCase()}</Tag>
          );
        }
      },
      width: '8%',
      filterMultiple: true,
      filters: [
        {
          text: 'Free',
          value: 'free',
        },
        {
          text: 'Pre',
          value: 'pre',
        },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Seat',
      align: 'center',
      render: (record) => {
        return <div>{record?.gumroad?.quantity}</div>
      },
      width: '8%',
    },
    {
      title: 'EndTime',
      align: 'center',
      dataIndex: 'endTime',
      width: '10%',
      sorter: (a, b) => moment(a.endTime).unix() - moment(b.endTime).unix(),
      defaultSortOrder: 'descend',
      render: (text, record, index) => {
        return (
          <div>
            <div>{moment(record.endTime).format('YYYY-MM-DD')}</div>
          </div>
        );
      }
    },
    {
      title: 'Expired',
      align: 'center',
      dataIndex: 'isAfter',
      render: (record) => {
        if (record === false) {
          return <Tag color="red">Expired</Tag>;
        }
      },
      width: '8%',
      filterMultiple: true,
      filters: [
        {
          text: 'Expired',
          value: false,
        },
      ],
      onFilter: (value, record) => record.isAfter === value,
    },
    {
      width: '8%',
      key: "action",
      render: (record) =>
        <div>
          {(user?.role === "super") && (
            <>
              <Button
                style={{ color: 'red' }}
                type='link'
                ghost
                onClick={() => {
                  Modal.confirm({
                    title: 'Confirm',
                    content: 'Are you sure to delete this user?',
                    okText: 'Ok',
                    cancelText: 'Không',
                    onOk: () => {
                      let { userId, email } = record;
                      let payload = {
                        userId,
                        email,
                      }
                      userDelete(payload)
                        .then(async (result) => {
                          message.success('This user has been deleted successfully');
                          setUsers([]);
                          setUsersFilter([]);
                          setLastVisible(null);
                          setHasMore(false);
                          fetchTeamUsers(false);
                        })
                        .catch(err => console.log(err))
                    }
                  });
                }}
              >
                Delete
              </Button>
            </>
          )}
        </div>
    },
  ];

  const columnsUser = [
    {
      title: "Created At",
      align: "center",
      dataIndex: "createdAt",
      width: "10%",
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      defaultSortOrder: 'descend',
      render: (text, record, index) => {
        return (
          <div>
            <div>{moment(record.createdAt).format('YYYY-MM-DD HH:mm')}</div>
          </div>
        );
      }
    },
    {
      title: "Email",
      width: "35%",
      ...getColumnSearchProps("email"),
    },
    {
      title: 'Role',
      align: 'center',
      dataIndex: 'role',
      render: (record) => {
        if (record === 'free') {
          return <Tag>{record.toUpperCase()}</Tag>;
        } else {
          return <Tag color="green">{record.toUpperCase()}</Tag>;
        }
      },
      width: '8%',
      filterMultiple: true,
      filters: [
        {
          text: 'Free',
          value: 'free',
        },
        {
          text: 'Trial',
          value: 'trial',
        },
        {
          text: 'Pre',
          value: 'pre',
        },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'EndTime',
      align: 'center',
      dataIndex: 'endTime',
      width: '10%',
      sorter: (a, b) => moment(a.endTime).unix() - moment(b.endTime).unix(),
      defaultSortOrder: 'descend',
      render: (text, record, index) => {
        return (
          <div>
            <div>{moment(record?.endTime).format('YYYY-MM-DD')}</div>
          </div>
        );
      }
    },
  ];

  return (
    <Layout className="jobs-page">
      <Layout style={{ paddingLeft: 24, paddingRight: 24, marginTop: 20 }} >
        <Card style={{ marginBottom: 18 }}>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} justify="space-between">
            <Col className="gutter-row" span={12}>
              <h2>Teams</h2>
            </Col>
          </Row>
          <Row>
            <Col span={24} className='flex items-start gap-4'>
              <RangePicker
                ranges={{
                  'Today': [moment(), moment()],
                  'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                  'This Week': [moment().startOf('week'), moment().endOf('week')],
                  'This Month': [moment().startOf('month'), moment().endOf('month')],
                  'This Quarter': [moment().startOf('quarter'), moment().endOf('quarter')],
                  'This Year': [moment().startOf('year'), moment().endOf('year')],
                }}
                onChange={onChange}
                defaultValue={[moment().subtract(12, 'months'), moment()]}
                allowClear={false}
              />

              <Input
                placeholder="Email"
                style={{ width: 120 }}
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
              />

              <Button
                type="primary"
                icon={<SearchOutlined />}
                disabled={!fromDate || !toDate || loading}
                onClick={() => {
                  setUsers([]);
                  setUsersFilter([]);
                  setLastVisible(null);
                  setHasMore(false);
                  fetchTeamUsers(false);
                }}
                className="flex items-center justify-center"
              >
                Search
              </Button>

              {hasMore && (
                <Button onClick={() => fetchTeamUsers(true)} disabled={loading}>
                  Load more
                </Button>
              )}
            </Col>
          </Row>
        </Card>

        <Table
          loading={loading}
          bordered
          columns={columns.filter(column => {
            if (column.key === "action") {
              if (user?.role === "super" || user?.role === "sale") {
                return true;
              }
              return false;
            }
            return true;
          })}
          dataSource={usersFilter}
          pagination={{
            defaultPageSize: 20
          }}
          rowKey={(row) => row.userId}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                loading={loading}
                bordered
                columns={columnsUser}
                dataSource={record.teams}
                pagination={false}
              />
            ),
            onExpand: onExpand,
            expandedRowKeys: [...expandedKey]
          }}
        />
      </Layout>
    </Layout>
  );
};

export default Users;